import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
// @mui
import {
  Box,
  List,
  Badge,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemButton,
  Stack,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
// _mock_
import { _notifications } from '../../../_mock';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { useDispatch, useSelector } from '../../../redux/store';
import { getInbox, getUnreadInboxCount, setUnreadInboxCount } from '../../../redux/slices/lookup';
import useInterval from '../../../hooks/useInterval';
import LinearLoader from '../../../components/LinearLoader';
import useLocales from '../../../hooks/useLocales';

// ----------------------------------------------------------------------

const _mockData = [
  {
    id: '1',
    title: 'Deposit received successfully',
    description: '',
    activity_type: 'deposit',
    read: false,
    created: new Date(Date.now() - 5 * 60 * 1000), // 5 mins ago
  },
  {
    id: '2',
    title: 'Withdrawal request approved',
    description: '',
    activity_type: 'withdrawal',
    read: true,
    created: new Date(Date.now() - 60 * 60 * 1000), // 1 hour ago
  },
  {
    id: '3',
    title: 'You have a new message from support',
    description: '',
    activity_type: 'chat_message',
    read: false,
    created: new Date(Date.now() - 10 * 60 * 1000), // 10 mins ago
  },
  {
    id: '4',
    title: 'Weekly report is ready',
    description: '',
    activity_type: 'mail',
    read: true,
    created: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
  },
];

// ----------------------------------------------------------------------

export default function NotificationPopover({ isSideNav, ButtonIcon }) {
  const { translate } = useLocales();

  const dispatch = useDispatch();

  const { unreadInboxCount, inbox, memberBalance } = useSelector((x) => x.lookup);

  const [isLoading, setIsLoading] = useState(false);

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setIsLoading(true);
    getInbox().finally(() => {
      setIsLoading(false);
      dispatch(setUnreadInboxCount(0));
    });
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const unreadMessages = useMemo(() => inbox.filter((m) => !m.read), [inbox]);
  const readMessages = useMemo(() => inbox.filter((m) => m.read), [inbox]);

  useInterval(getUnreadInboxCount, 15000);

  return (
    <>
      {isSideNav ? (
        <Stack alignItems="center" onClick={handleOpen} sx={{ cursor: 'pointer' }}>
          <ButtonIcon src={require('../../../assets/navbar/notification.png')} />
          <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
            {translate('notifications')}
          </Typography>
        </Stack>
      ) : (
        <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 35, height: 35 }}>
          <Badge badgeContent={unreadInboxCount || 0} color="error">
            <Iconify icon="mdi:bell" />
          </Badge>
        </IconButtonAnimate>
      )}

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          width: 360,
          p: 0,
          mt: 1.5,
          ml: 0.75,
          border: '1px solid rgb(87, 43, 43)',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 1 }}>
            <LinearLoader />
          </Box>
        ) : (
          <>
            <Box sx={{ m: 1, borderRadius: 1, backgroundColor: 'rgb(39, 8, 8)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 1.5 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: '#fff' }}>
                    {translate('notifications')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f5f5f5' }}>
                    {translate('you_have_x_unread_messages', { x: unreadInboxCount })}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderStyle: 'dashed', borderColor: 'rgb(87, 43, 43)' }} />

            <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
              {unreadMessages?.length ? (
                <List
                  disablePadding
                  subheader={
                    <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline', color: '#fff' }}>
                      {translate('new')}
                    </ListSubheader>
                  }
                >
                  {unreadMessages.map((message) => (
                    <NotificationItem
                      key={message.id}
                      notification={message}
                      currencyCode={memberBalance?.currencyCode}
                    />
                  ))}
                </List>
              ) : (
                ''
              )}

              {readMessages?.length ? (
                <List
                  disablePadding
                  subheader={
                    unreadMessages?.length ? (
                      <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline', color: '#fff' }}>
                        {translate('before_that')}
                      </ListSubheader>
                    ) : null
                  }
                >
                  {readMessages.map((message) => (
                    <NotificationItem
                      key={message.id}
                      notification={message}
                      currencyCode={memberBalance?.currencyCode}
                    />
                  ))}
                </List>
              ) : (
                ''
              )}
            </Scrollbar>
          </>
        )}
        {/* <Divider sx={{ borderStyle: 'dashed' }} /> */}

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple>
            View All
          </Button>
        </Box> */}
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    created: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    read: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    activity_type: PropTypes.string,
    // avatar: PropTypes.any,
  }),
};

function NotificationItem({ notification, currencyCode }) {
  const { avatar, title } = renderContent(notification, currencyCode);

  return (
    <ListItemButton
      sx={{
        p: 1,
        mt: '1px',
        ...(!notification.read && {
          bgcolor: 'rgba(39, 8, 8, 0.5)',
        }),
      }}
    >
      <Box
        sx={{
          width: '100%',
          p: 1.5,
          borderRadius: 1,
          backgroundColor: 'rgba(39, 8, 8, 0.7)',
        }}
      >
        <ListItemText
          primary={
            <Typography variant="subtitle2" sx={{ color: '#fff' }}>
              {title}
            </Typography>
          }
          secondary={
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
              {fToNow(notification.created)}
            </Typography>
          }
        />
      </Box>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function getTitle(title, currencyCode) {
  const firstSpaceIndex = title.indexOf(' ');
  const secondSpaceIndex = title.indexOf(' ', firstSpaceIndex + 1);

  if (firstSpaceIndex !== -1) {
    const extractedText = title.substring(firstSpaceIndex + 1, secondSpaceIndex);
    return `${currencyCode} ${parseFloat(extractedText) * 1000} ${title.substring(secondSpaceIndex + 1)}`;
  }
  return '';
}

function renderContent(notification, currencyCode) {
  const _title = notification?.title;
  // if (
  //   (currencyCode?.toUpperCase() === 'MMK' || currencyCode?.toUpperCase() === 'VND') &&
  //   notification?.activity_type === 'deposit'
  // ) {
  //   _title = getTitle(notification?.title, currencyCode);
  // }

  const title = (
    <Typography variant="subtitle2">
      {_title}
      {/* <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {noCase(notification.description)}
      </Typography> */}
    </Typography>
  );

  if (notification.type === 'order_placed') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_package.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === 'order_shipped') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_shipping.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === 'mail') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_mail.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === 'chat_message') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_chat.svg"
        />
      ),
      title,
    };
  }
  return {
    avatar: notification.avatar ? <img alt={notification.title} src={notification.avatar} /> : null,
    title,
  };
}
